import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import settingsService from "api/settingsService";
import { ContactFormPublishMethod } from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import LinkIcon from "assets/icons/wizardLink.svg?react";
import IframeIcon from "assets/icons/wizardIframe.svg?react";
import CopyIcon from "assets/icons/copy.svg?react";
import styles from "../styles.module.scss";
import WizardActions from "../WizardActions";
import { useWizard } from "../WizardContext";

const METHODS: Array<{
  id: ContactFormPublishMethod;
  Icon: React.FC<React.SVGProps<SVGElement>>;
  labelKey: string;
  hintKey: string;
}> = [
  {
    id: "link",
    Icon: LinkIcon,
    labelKey: "contactFormWizard.steps.publish.linkLabel",
    hintKey: "contactFormWizard.steps.publish.linkHint",
  },
  {
    id: "iframe",
    Icon: IframeIcon,
    labelKey: "contactFormWizard.steps.publish.iframeLabel",
    hintKey: "contactFormWizard.steps.publish.iframeHint",
  },
  // {
  //   id: "eszyfrowane",
  //   Icon: ShareIcon,
  //   labelKey: "contactFormWizard.steps.publish.eszyfrowaneLabel",
  //   hintKey: "contactFormWizard.steps.publish.eszyfrowaneHint",
  // },
];

const Step6Publish = () => {
  const { t } = useTranslation();
  const { settings, patchSettings, logoKey, finish, inline } = useWizard();

  const { data: formData } = settingsService.useGetForm(settings.WorkspaceId);
  const key = formData?.data?.[0]?.Key ?? logoKey;
  const base = import.meta.env.VITE_APP_FORM_SITE;
  const link = useMemo(() => (key ? `${base}?id=${key}` : ""), [key, base]);

  const snippet = useMemo(() => {
    switch (settings.PublishMethod) {
      case "iframe":
        return `<iframe src="${link}" width="1072px" height="416px"></iframe>`;
      case "eszyfrowane":
        return link;
      case "link":
      default:
        return link;
    }
  }, [settings.PublishMethod, link]);

  const handleCopy = async () => {
    if (!snippet) return;
    await navigator.clipboard.writeText(snippet);
    notification({
      type: ENotificationType.INFO,
      title: t("settings.copiedToClipboard"),
      text: t("settings.embedCodeCopied"),
    });
  };

  const iframeSnippet = link
    ? `<iframe src="${link}" width="1072px" height="416px"></iframe>`
    : "";

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (inline) {
    return (
      <div className={styles.inlinePublishActions}>
        <button
          type="button"
          className={styles.inlineDownloadBtn}
          onClick={() => downloadFile(iframeSnippet, "formularz-iframe.html")}
          disabled={!iframeSnippet}
        >
          Pobierz kod iFrame
        </button>
      </div>
    );
  }

  return (
    <>
      <div className={styles.hdr}>
        {t("contactFormWizard.steps.publish.methodHeader")}
      </div>
      <div className={styles.description}>
        {t("contactFormWizard.steps.publish.methodHint")}
      </div>

      <div className={styles.publishGrid}>
        {METHODS.map(({ id, Icon, labelKey, hintKey }) => (
          <div
            key={id}
            className={classNames(
              styles.publishCard,
              settings.PublishMethod === id && styles.selected,
            )}
            onClick={() => patchSettings({ PublishMethod: id })}
            role="button"
          >
            <Icon className={styles.publishIcon} />
            <div className={styles.publishTitle}>{t(labelKey)}</div>
            <div className={styles.publishHint}>{t(hintKey)}</div>
          </div>
        ))}
      </div>

      <div className={styles.hdr}>
        {t("contactFormWizard.steps.publish.snippetHeader")}
      </div>
      <div className={styles.description}>
        {t("contactFormWizard.steps.publish.snippetBody")}
      </div>

      <div className={styles.copyBlock}>
        <div className={styles.code}>
          <span className={styles.codeText}>{snippet || "—"}</span>
          <button
            type="button"
            className={styles.codeCopyBtn}
            onClick={handleCopy}
            aria-label={t("common.copy")}
            title={t("common.copy")}
            disabled={!snippet}
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      <WizardActions
        finishLabelKey="contactFormWizard.steps.publish.publish"
        onFinish={finish}
      />
    </>
  );
};

export default Step6Publish;
